import { type CSSProperties, type DragEvent } from 'react';
import { GripVertical, UserPlus } from 'lucide-react';

import type { SeatView } from '@/api/seats.api';
import { Badge } from '@/components/ui/Badge';
import { cn } from '@/lib/cn';
import { departmentDotColor, roleLabel, type SeatGwc } from '@/lib/accountability/seatMeta';

/**
 * A single node on the accountability chart: the seat name, its holder (or
 * a Vacant / Hiring badge), a role badge, a department dot, the measurables
 * count, and — when a recent People Analyzer round has data — a small GWC
 * indicator on the holder. Admins get a drag handle to reparent.
 *
 * Purely presentational: the parent owns drag orchestration and passes the
 * handlers through. Fixed dimensions so the d3 layout can position it
 * absolutely; `NODE_WIDTH` / `NODE_HEIGHT` are the single source of truth
 * for both this card and the chart's layout math.
 */

export const NODE_WIDTH = 248;
export const NODE_HEIGHT = 132;

/** Custom MIME so unrelated drags (e.g. a text selection) can't trigger a
 *  reparent — the drop handlers check `dataTransfer.types` for this. */
export const SEAT_DRAG_MIME = 'application/x-kw-seat-id';

export interface SeatCardProps {
  seat: SeatView;
  departmentName?: string | null;
  gwc?: SeatGwc | null;
  canManage?: boolean;
  isDragging?: boolean;
  isDropTarget?: boolean;
  /** Absolute-position style from the chart layout. Omit for flow layout. */
  style?: CSSProperties;
  onSelect?: () => void;
  onDragStartSeat?: (seatId: string) => void;
  onDragEndSeat?: () => void;
  onDropSeat?: (draggedSeatId: string, targetSeatId: string) => void;
  onDragOverSeat?: (targetSeatId: string) => void;
  onDragLeaveSeat?: () => void;
}

export function SeatCard({
  seat,
  departmentName,
  gwc,
  canManage = false,
  isDragging = false,
  isDropTarget = false,
  style,
  onSelect,
  onDragStartSeat,
  onDragEndSeat,
  onDropSeat,
  onDragOverSeat,
  onDragLeaveSeat,
}: SeatCardProps) {
  const holder = seat.holderEmployee;
  const initials = holder
    ? `${holder.firstName.charAt(0)}${holder.lastName.charAt(0)}`.toUpperCase() || '·'
    : null;

  function handleDragStart(e: DragEvent<HTMLDivElement>): void {
    if (!canManage) {
      e.preventDefault();
      return;
    }
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData(SEAT_DRAG_MIME, seat.id);
    e.dataTransfer.setData('text/plain', seat.id);
    onDragStartSeat?.(seat.id);
  }

  function handleDragOver(e: DragEvent<HTMLDivElement>): void {
    if (!canManage) return;
    if (!Array.from(e.dataTransfer.types).includes(SEAT_DRAG_MIME)) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    onDragOverSeat?.(seat.id);
  }

  function handleDrop(e: DragEvent<HTMLDivElement>): void {
    if (!canManage) return;
    e.preventDefault();
    const draggedId = e.dataTransfer.getData(SEAT_DRAG_MIME);
    if (!draggedId || draggedId === seat.id) return;
    onDropSeat?.(draggedId, seat.id);
  }

  return (
    <div
      data-testid={`seat-card-${seat.id}`}
      role="button"
      tabIndex={0}
      aria-label={`Seat: ${seat.name}`}
      draggable={canManage}
      style={style}
      onDragStart={handleDragStart}
      onDragEnd={onDragEndSeat}
      onDragOver={handleDragOver}
      onDragLeave={onDragLeaveSeat}
      onDrop={handleDrop}
      onClick={onSelect}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onSelect?.();
        }
      }}
      className={cn(
        'flex cursor-pointer flex-col gap-1.5 rounded-kw-lg border bg-white p-3 text-left shadow-kw-xs transition-colors',
        'print:rounded-none print:border-black print:shadow-none',
        isDragging
          ? 'border-kw-cerulean opacity-50'
          : isDropTarget
            ? 'border-kw-cerulean bg-kw-bg-blue ring-2 ring-kw-cerulean/30'
            : 'border-kw-border hover:border-kw-cerulean/40',
        canManage && 'active:cursor-grabbing',
      )}
    >
      {/* Seat name + drag handle */}
      <div className="flex items-start gap-1.5">
        {canManage ? (
          <span className="-ml-0.5 mt-0.5 text-kw-ghost print:hidden" aria-hidden>
            <GripVertical className="size-3.5" />
          </span>
        ) : null}
        <p className="min-w-0 flex-1 truncate text-[13px] font-semibold text-kw-shark" title={seat.name}>
          {seat.name}
        </p>
      </div>

      {/* Holder or Vacant/Hiring */}
      {holder ? (
        <div className="flex items-center gap-2">
          <span className="grid size-7 shrink-0 place-items-center rounded-kw-full bg-kw-cerulean text-[11px] font-medium text-white print:bg-white print:text-black print:ring-1 print:ring-black">
            {initials}
          </span>
          <div className="min-w-0 flex-1">
            <p className="truncate text-[12px] font-medium text-kw-shark">
              {holder.firstName} {holder.lastName}
            </p>
            {holder.jobTitle ? (
              <p className="truncate text-[11px] text-kw-muted">{holder.jobTitle}</p>
            ) : holder.membershipId === null ? (
              <p className="truncate text-[11px] italic text-kw-muted">Directory only</p>
            ) : null}
          </div>
          {gwc ? <GwcIndicator gwc={gwc} /> : null}
        </div>
      ) : (
        <div className="flex items-center gap-2">
          <span className="grid size-7 shrink-0 place-items-center rounded-kw-full border border-dashed border-kw-border text-kw-ghost print:border-black">
            <UserPlus className="size-3.5" />
          </span>
          {seat.hiring ? <Badge variant="warning">Hiring</Badge> : <Badge variant="neutral">Vacant</Badge>}
        </div>
      )}

      {/* Footer: role · department · measurables */}
      <div className="mt-auto flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px] text-kw-muted print:text-black">
        <span className="inline-flex items-center rounded-kw-full bg-kw-bg px-1.5 py-0.5 font-medium text-kw-shark print:bg-white print:ring-1 print:ring-black">
          {roleLabel(seat.systemRole)}
        </span>
        <span className="inline-flex items-center gap-1" title={departmentName ?? 'No department'}>
          <span
            className="size-2 rounded-full"
            style={{ backgroundColor: departmentDotColor(seat.departmentId) }}
            aria-hidden
          />
          <span className="max-w-[80px] truncate">{departmentName ?? 'No dept'}</span>
        </span>
        {seat.metricsCount > 0 ? (
          <span>
            {seat.metricsCount} measurable{seat.metricsCount === 1 ? '' : 's'}
          </span>
        ) : null}
      </div>
    </div>
  );
}

function GwcIndicator({ gwc }: { gwc: SeatGwc }) {
  const items: { key: keyof SeatGwc; letter: string; label: string }[] = [
    { key: 'getsIt', letter: 'G', label: 'Gets it' },
    { key: 'wantsIt', letter: 'W', label: 'Wants it' },
    { key: 'capacity', letter: 'C', label: 'Capacity' },
  ];
  return (
    <span className="flex shrink-0 gap-0.5" aria-label="GWC">
      {items.map(({ key, letter, label }) => {
        const v = gwc[key];
        return (
          <span
            key={key}
            title={`${label}: ${v === null ? 'unrated' : v ? 'yes' : 'no'}`}
            className={cn(
              'grid size-4 place-items-center rounded-full text-[9px] font-bold',
              v === true
                ? 'bg-kw-emerald-light text-kw-tag-text'
                : v === false
                  ? 'bg-kw-danger-bg text-kw-danger-text'
                  : 'bg-kw-bg text-kw-ghost',
            )}
          >
            {letter}
          </span>
        );
      })}
    </span>
  );
}
